import type { LocationQuery, RouteLocationNormalizedGeneric } from '#vue-router';

function billingAddressHasDefaultCountry() {
  const { defaultCountry } = useMainStore();
  const { getBillingAddress: cartBillingAddress } = useCartStore();

  if (cartBillingAddress && defaultCountry && cartBillingAddress.country?.isocode !== defaultCountry) {
    return cartBillingAddress?.country?.isocode === defaultCountry;
  }

  return true;
}

async function redirectToPaymentOrSkipToSummary(query: LocationQuery) {
  const { runWithContext } = useNuxtApp();
  const cartStore = useCartStore();
  const userStore = useUserStore();
  const { prepareStoreAsDeliveryOption } = useDeliveryHelpers();

  // If cart is not eligible for C&C or PuDo, go to address selection
  if (
    (!cartStore.getIsClickAndCollectDeliveryAddressTypeEnabled && cartStore.getDeliveryAddress?.storeInfo) ||
    (!cartStore.getIsPickupStationDeliveryAddressTypeEnabled && cartStore.getDeliveryAddress?.pickUpStation)
  ) {
    return navigateToWithAdobeParam('/checkout/address/', query);
  }

  // If Click & Collect is enabled and default shipping address has remarks(storeId)
  // Check Product availability and set pickup address if valid.
  if (cartStore.getIsClickAndCollectDeliveryAddressTypeEnabled) {
    const storeId = userStore.getDefaultShippingAddress?.remarks;
    const isPickupStation = userStore.getDefaultShippingAddress?.pickUpStation;
    const isStore = userStore.getDefaultShippingAddress?.storeInfo;

    if (isStore && storeId && !isPickupStation) {
      const productsAvailability = await cartStore.findCartStoreAvailability(storeId);

      const validAvailability = [PRODUCT_ALL_AVAILABLE, PRODUCT_NOT_ALL_AVAILABLE];
      const isAvailable = validAvailability.includes(productsAvailability as string);

      if (isAvailable) {
        const defaultShippingAddress = userStore.getDefaultShippingAddress;
        cartStore.pickupAddress.fnStore = prepareStoreAsDeliveryOption(defaultShippingAddress);
      }
    }
  }

  return runWithContext(() => {
    if (cartStore.checkIsPaymentStateInvalid(cartStore.getPaymentMode)) {
      return navigateToWithAdobeParam('/checkout/payment/', query);
    }

    return navigateToWithAdobeParam('/checkout/summary/', query);
  });
}

async function checkRedirectsCheckout(to: RouteLocationNormalizedGeneric) {
  const { $i18n, runWithContext } = useNuxtApp();
  const userStore = useUserStore();
  const cartStore = useCartStore();
  const { query } = to;

  // Bypass checkout for store editors
  if (userStore.user?.isStoreEditor) {
    return navigateToWithAdobeParam($i18n.t('url.storeEdit.full'), query);
  }

  /**
   * Load latest cart data and handle other redirect conditions.
   * Do not wrap it in the Promise.all().
   * loadDeliveryModes() v3 updates cart obj on API side, its need to be completed
   * first in order to get latest cart data.
   */
  await cartStore.loadDeliveryModes();
  await cartStore.loadCart({ fields: 'FULL' });

  return runWithContext(async () => {
    if (!cartStore.getReachedMinimumOrderValue) {
      return navigateToWithAdobeParam('/cart/', query);
    }

    if (!cartStore.getPhoneNumberIsValid) {
      return navigateToWithAdobeParam('/checkout/address/', query);
    }

    if (!billingAddressHasDefaultCountry()) {
      return navigateToWithAdobeParam('/checkout/address/', query);
    }

    if (!cartStore.getBillingAddress || !cartStore.getDeliveryAddress) {
      if (userStore.getIsAuthenticated && userStore.getBillingAddress) {
        if (await cartStore.autoFillCart()) {
          return runWithContext(() => redirectToPaymentOrSkipToSummary(query));
        }
        return runWithContext(() => navigateToWithAdobeParam('/checkout/address/', query));
      }

      if (cartStore.getIsGuest || (userStore.getIsAuthenticated && !userStore.getBillingAddress)) {
        return navigateToWithAdobeParam('/checkout/address/', query);
      }

      return navigateToWithAdobeParam('/checkout/start/', query);
    }

    if (cartStore.getDeliveryAddressIsPickupStore) {
      try {
        await userStore.verifyAddress(cartStore.getDeliveryAddress);
      } catch (error) {
        return runWithContext(() =>
          navigateToWithAdobeParam('/checkout/address/', { ...query, error: 'invalidPickupStation' }),
        );
      }
    }

    return runWithContext(() => redirectToPaymentOrSkipToSummary(query));
  });
}

/**
 * Global middleware to handle redirects on `/checkout` route without having to run all other middlewares first.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  if (to.name === 'checkout') {
    return useNuxtApp().runWithContext(() => checkRedirectsCheckout(to));
  }
});
